import type { TSmartAck } from 'types/smartAcks.types';
import type { TTextBubbleItem } from 'components/TextBubbleDragAndDrop';
import {
  type TAvailableLanguageCodes,
  type TAnyClientQuestionType,
  EQuestionType,
  type TFormType,
  EFormType,
} from '@hubertai/screening-question-types';

export enum questionTextMessageType {
  Intro = 'intro',
  Text = 'text',
  Outro = 'outro',
  Clarify = 'clarify',
}

export const CreateableQuestionTypes = [
  EQuestionType.Info,
  EQuestionType.SingleSelect,
  EQuestionType.MultipleSelect,
  EQuestionType.Open,
  EQuestionType.Form,
  EQuestionType.DatePicker,
  EQuestionType.MonthPicker,
  EQuestionType.Ranking,
  EQuestionType.Audio,
  EQuestionType.Video,
  EQuestionType.Scheduling,
];

export type TQuestionConversationSettings = {
  smart_ack?: Pick<TSmartAck, 'id' | 'active'>;
};

export const FormTypesList: TFormType[] = [
  EFormType.Text,
  EFormType.Number,
  EFormType.Email,
  EFormType.Slider,
  EFormType.Checkbox,
  EFormType.Range,
  EFormType.Matrix,
  EFormType.File,
];

// Keys of forms
// Used in formActions.ts, FormFieldUpdater, and CreateQuestion context
export type TFormKeysV4 =
  | 'help_text'
  | 'placeholder'
  | 'label'
  | 'label_left'
  | 'label_right'
  | 'unit'
  | 'min'
  | 'max'
  | 'step'
  | 'enabled'
  | 'is_mandatory'
  | 'evaluation';

export type TIsDefaultValue = { isDefault: boolean; value: any };

export const QuestionTypeLabels: Record<EQuestionType | EFormType, string> = {
  [EQuestionType.Info]: 'Message',
  [EQuestionType.SingleSelect]: 'Single select',
  [EQuestionType.MultipleSelect]: 'Multiple select',
  [EQuestionType.Open]: 'Open ended',
  [EQuestionType.Form]: 'Form',
  [EQuestionType.DatePicker]: 'Date picker',
  [EQuestionType.MonthPicker]: 'Month picker',
  [EQuestionType.Calendly]: 'Calendly',
  [EQuestionType.Reusable]: 'Reusable',
  [EQuestionType.Gaps]: 'Fill in the gaps',
  [EFormType.Text]: 'Text input',
  [EFormType.Number]: 'Number input',
  [EFormType.Email]: 'Email input',
  [EFormType.Slider]: 'Slider',
  [EFormType.Range]: 'Range',
  [EFormType.Matrix]: 'Matrix',
  [EFormType.File]: 'File upload',
  [EFormType.Checkbox]: 'Checkbox',
  [EQuestionType.Ranking]: 'Ranking',
  [EQuestionType.Audio]: 'Audio (voice recording)',
  [EQuestionType.Video]: 'Video recording',
  [EFormType.Phone]: 'Phone input',
  [EQuestionType.Scheduling]: 'Scheduling (cal.com)',
};

export const QuestionTypeDescriptions: Record<EQuestionType | EFormType, string> = {
  [EQuestionType.Info]: 'Non-interactive message for context or instructions; collects no answer.',
  [EQuestionType.SingleSelect]: 'One choice from a list; use for mutually exclusive options and simple branching.',
  [EQuestionType.MultipleSelect]: 'Multiple choices from a list; supports min/max limits and "Other".',
  [EQuestionType.Open]: 'Free-text answer; use for narratives or specifics; supports intents and clarifications.',
  [EQuestionType.Form]: 'Grouped form fields (text, number, email, slider, range, matrix, checkbox, file) in one step.',
  [EQuestionType.DatePicker]: 'Pick a specific date; common for availability or birthdates.',
  [EQuestionType.MonthPicker]: 'Pick a month; use when only month/year precision is needed.',
  [EQuestionType.Calendly]: 'Embedded Calendly booking step to schedule interviews; no scoring.',
  [EQuestionType.Reusable]: 'Reference to a reusable question block shared across templates.',
  [EQuestionType.Gaps]: 'Fill-in-the-blank text; candidate completes missing parts within a sentence.',
  [EFormType.Text]: 'Short free‑text input; use for names or brief responses; supports placeholder and required.',
  [EFormType.Number]: 'Numeric input with min/max and step; use for counts, years, or amounts; validation enforced.',
  [EFormType.Email]:
    'Email field with format validation; use to collect contact emails; supports placeholder and required.',
  [EFormType.Slider]:
    'Single value slider between min/max with unit and left/right labels; supports weighted or conditional evaluation.',
  [EFormType.Range]:
    'Two-ended range selection with min/max and unit; ideal for availability or preferred ranges; supports conditional evaluation.',
  [EFormType.Matrix]:
    'Row × column grid to rate multiple items across a scale; column cells can carry evaluation logic.',
  [EFormType.File]:
    'Upload a file (e.g., CV, certificate); collected as an attachment; size/type limits handled by platform.',
  [EFormType.Checkbox]: 'Single boolean toggle; use for confirmations or consent; can be required to proceed.',
  [EQuestionType.Ranking]: 'Ranking question; use to rank items from a list.',
  [EQuestionType.Audio]: 'Candidate records a voice answer; use to assess spoken-language ability.',
  [EQuestionType.Video]: 'Candidate records a face-front video answer; assess spoken language and presentation.',
  [EFormType.Phone]: 'Phone input; use to collect phone numbers; supports placeholder and required.',
  [EQuestionType.Scheduling]:
    'cal.com booking step: embed in chat, spoken slot offers on phone; emails a booking link as fallback. No scoring.',
};

export type TextData = Partial<Record<TAvailableLanguageCodes, { isDefault: boolean; value: string[] }>>;

export type TextDataWithValueIds = Partial<
  Record<TAvailableLanguageCodes, { isDefault: boolean; value: TTextBubbleItem[] }>
>;

export type TQuestionConfigDTO = {
  templateLanguage: TAvailableLanguageCodes | undefined;
  questionLanguage: TAvailableLanguageCodes;
  questionV4: TAnyClientQuestionType;
  preActions: TIsDefaultValue;
  postActions: TIsDefaultValue;
  intros?: TextData;
  texts?: TextData;
  outros?: TextData;
  clarifies?: TextData;
  publishedOrNonExclusive: boolean;
  hasQuestionUpdatePermission: boolean;
};
